import { useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useRouteContext,
} from "@tanstack/react-router";
import { Button, Card } from "react-bootstrap";
import { GridCustomCellProps } from "@progress/kendo-react-grid";
import { LuMail, LuUserPlus, LuX } from "react-icons/lu";
import { FiBarChart2 } from "react-icons/fi";

import { DownloadBlob } from "@/components/components";
import { User, UserOrg } from "@/models/user.tsx";
import {
  useUsers,
  useEntity,
  useChildEntities,
} from "@/queries.ts";
import { fetchAuthenticated } from "@/queries/shared";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";
import Loader from "@/components/shared/Loader";
import { AddUserWizard } from "@/components/user-management/components/AddUserWizard/AddUserWizard";

const usersAdminTableStyles = `
    .users-admin-table .k-grid { width: 100% !important; }
    .users-admin-table .k-grid tbody tr { cursor: pointer; }
    .users-admin-table .k-grid tbody tr:hover { background-color: rgba(0,0,0,0.03); }
`;

function EmailCell(props: GridCustomCellProps) {
  const email: string = props.dataItem.email ?? "";
  return (
    <td
      {...stripAriaSelected(props.tdProps)}
      style={{ ...props.tdProps?.style, verticalAlign: "middle" }}
    >
      <span className="d-flex align-items-center gap-2">
        <LuMail
          size={14}
          style={{ color: "#64748B", flexShrink: 0 }}
          aria-hidden="true"
        />
        <span className="text-truncate">{email || "—"}</span>
      </span>
    </td>
  );
}

const userColumns = [
  { field: "firstName", title: "First Name", minWidth: 150 },
  { field: "lastName", title: "Last Name", minWidth: 150 },
  { field: "userName", title: "Username", minWidth: 200 },
  {
    field: "email",
    title: "Email",
    minWidth: 250,
    cells: { data: EmailCell },
  },
];

export default function UsersAdminPage() {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const params = useParams({ strict: false });
  const [showAddUserWizard, setShowAddUserWizard] = useState(false);
  const isBeta4 = leaContext.hasFeature("UserManagement");

  const isStateLevel = params.leaid === "0-";
  const hasStateLevelAccess = leaContext.isStateLevelAdmin;
  const statewideResult = useUsers("0-", hasStateLevelAccess);
  const allUsers: User<UserOrg>[] = statewideResult.data ?? [];
  const esiIdPath = leaContext.currentUserOrg?.esiIdPath ?? "";
  const entityResult = useEntity(esiIdPath);
  const childrenResult = useChildEntities(esiIdPath);

  if (!leaContext.hasRole("ADMIN")) return null;

  const migrateUsers = async () => {
    const usersData = await fetchAuthenticated(
      leaContext as Parameters<typeof fetchAuthenticated>[0],
      `/json/account/${params.leaid}/users/migrate`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    DownloadBlob(await usersData.blob(), "users.csv");
  };

  if (!isBeta4 && params.leaid !== "0-") {
    return (
      <LayoutContentArea headingText="User Management">
        Content in development
      </LayoutContentArea>
    );
  }

  return (
    <LayoutContentArea
      headingText={
        <div>
          <h1 className="fs-4 mb-0">User Management</h1>
          <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.925rem" }}>
            Manage user accounts, roles, and permissions
          </p>
        </div>
      }
      headerActions={
        <div className="d-flex align-items-center gap-2">
          {isStateLevel && (
            <Link
              to="/$leaid/admin/reports"
              params={{ leaid: params.leaid ?? "" }}
              search={{ report: "2.2" }}
              className="btn btn-outline-primary d-flex align-items-center gap-2"
            >
              <FiBarChart2 size={16} />
              User Management Extract Report
            </Link>
          )}
          {params.leaid === "0-" && (
            <Button
              variant="outline-primary"
              onClick={() =>
                migrateUsers().catch((err) => {
                  console.error("Error downloading extract:", err);
                })
              }
            >
              Migrate CAPSDAC 1.0 Users
            </Button>
          )}
          <Button
            className="d-flex align-items-center gap-2"
            style={{
              background: "var(--color-p3)",
              border: "none",
              color: "#fff",
            }}
            onClick={() => setShowAddUserWizard(true)}
          >
            <LuUserPlus size={16} />
            Add New User
          </Button>
        </div>
      }
    >
      <AddUserWizard
        show={showAddUserWizard}
        onClose={() => setShowAddUserWizard(false)}
        allUsers={allUsers}
        isStateLevel={isStateLevel}
        leaid={params.leaid ?? ""}
        vendorNumber={leaContext.currentUserOrg?.vendorNumber}
        coreData={leaContext.coreData}
        entityResult={entityResult}
        childrenResult={childrenResult}
      />

      <UsersAdminTable />
    </LayoutContentArea>
  );
}

export function UsersAdminTable() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const [firstNameInput, setFirstNameInput] = useState("");
  const [lastNameInput, setLastNameInput] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [tableResetKey, setTableResetKey] = useState(0);

  const result = useUsers(params.leaid ?? "");

  if (!params.leaid) {
    return <Loader containerClass="py-5" />;
  }

  const handleClear = () => {
    setFirstNameInput("");
    setLastNameInput("");
    setUsernameInput("");
    setEmailInput("");
    setTableResetKey((k) => k + 1);
  };

  const rawData: User<UserOrg>[] = result.data ?? [];

  const filteredData = rawData.filter((u) => {
    return (
      (!firstNameInput ||
        (u.firstName ?? "")
          .toLowerCase()
          .includes(firstNameInput.toLowerCase())) &&
      (!lastNameInput ||
        (u.lastName ?? "")
          .toLowerCase()
          .includes(lastNameInput.toLowerCase())) &&
      (!usernameInput ||
        (u.userName ?? "")
          .toLowerCase()
          .includes(usernameInput.toLowerCase())) &&
      (!emailInput ||
        (u.email ?? "").toLowerCase().includes(emailInput.toLowerCase()))
    );
  });

  return (
    <>
      <Card className="mb-4 no-padding card-border shadow">
        <Card.Body className="p-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md">
              <label
                htmlFor="first-name-filter"
                className="form-label fw-semibold small mb-1"
              >
                First Name
              </label>
              <input
                id="first-name-filter"
                type="text"
                className="form-control"
                placeholder="Search first name..."
                value={firstNameInput}
                onChange={(e) => setFirstNameInput(e.target.value)}
              />
            </div>
            <div className="col-12 col-md">
              <label
                htmlFor="last-name-filter"
                className="form-label fw-semibold small mb-1"
              >
                Last Name
              </label>
              <input
                id="last-name-filter"
                type="text"
                className="form-control"
                placeholder="Search last name..."
                value={lastNameInput}
                onChange={(e) => setLastNameInput(e.target.value)}
              />
            </div>
            <div className="col-12 col-md">
              <label
                htmlFor="username-filter"
                className="form-label fw-semibold small mb-1"
              >
                Username
              </label>
              <input
                id="username-filter"
                type="text"
                className="form-control"
                placeholder="Search username..."
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
              />
            </div>
            <div className="col-12 col-md">
              <label
                htmlFor="email-filter"
                className="form-label fw-semibold small mb-1"
              >
                Email
              </label>
              <input
                id="email-filter"
                type="text"
                className="form-control"
                placeholder="Search email..."
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-auto d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center gap-1"
                onClick={handleClear}
              >
                <LuX size={15} />
                Clear
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <style>{usersAdminTableStyles}</style>
      <Card className="table-style mb-3" style={{ width: "100%" }}>
        {result.isPending ? (
          <div className="py-5 d-flex flex-column align-items-center gap-2">
            <Loader containerClass="" />
            <span className="text-muted small">Loading users...</span>
          </div>
        ) : (
          <div className="users-admin-table">
            <KendoTableClientState
              key={`users-table-${params.leaid}-${tableResetKey}`}
              data={filteredData}
              columns={userColumns}
              error={result.error}
              noItemsMessage="No users found."
              filterable={false}
              onRowClick={(e) => {
                const userName = e.dataItem.userName;
                if (!userName) return;
                navigate({
                  to: "/$leaid/admin/users/$userid",
                  params: { leaid: params.leaid!, userid: userName },
                });
              }}
            />
          </div>
        )}
      </Card>
    </>
  );
}
